import {
  EnvironmentType,
  LoginMethod,
  SSOBaseURL,
  TokenTypes,
  appRootURL,
  mapBaseURL,
  mediaBaseURL,
  surveyBaseURL,
  userBaseURL,
} from "@/constants";
import type { Authentication } from "@/features/auth/types";
import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import { saveLocalUser } from "./authentication";

const redirectToLogin = () => {
  localStorage.setItem("returnUrl", window.location.pathname);
  switch (EnvironmentType) {
    case "local":
    case "development": {
      const loginUrl = new URL(`${appRootURL}/login`);
      if (loginUrl.protocol !== "https:" && loginUrl.protocol !== "http:") {
        throw new Error("Invalid app root URL");
      }
      window.location.assign(
        loginUrl.origin + loginUrl.pathname + loginUrl.search + loginUrl.hash,
      );
      break;
    }
    default:
      if (SSOBaseURL) {
        const parsed = new URL(SSOBaseURL);
        if (parsed.protocol !== "https:") {
          throw new Error("SSO Base URL must use HTTPS");
        }
        window.location.assign(
          parsed.origin + parsed.pathname + parsed.search + parsed.hash,
        );
      } else {
        throw new Error("No SSO Path defined");
      }
  }
};

// Factory to create a fetchBaseQuery with shared header logic
const createBaseQuery = (baseUrl: string, addAccessToken = true) =>
  retry(
    fetchBaseQuery({
      baseUrl,
      prepareHeaders: (headers) => {
        const accessToken = localStorage.getItem("accessToken");
        if (accessToken && addAccessToken) {
          headers.set(TokenTypes.AccessToken, accessToken);
        }
        return headers;
      },
    }),
    { maxRetries: 3 },
  );

// Shared refresh logic wrapper with explicit types
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query";

const createBaseQueryWithReauth =
  (
    baseQuery: ReturnType<typeof createBaseQuery>,
    refreshBaseQuery: ReturnType<typeof createBaseQuery>,
  ): BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Record<string, unknown>,
    FetchBaseQueryMeta
  > =>
  async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        const refreshResult = await refreshBaseQuery(
          {
            url: "authentication/refresh",
            method: "POST",
            body: {
              token: refreshToken,
              redirectUrl: `${appRootURL}/sso-callback`,
              authType: LoginMethod.SSO,
            },
          },
          api,
          extraOptions,
        );
        if (refreshResult.data) {
          const data = refreshResult.data as Authentication;
          saveLocalUser({
            ...data,
            ...data.user,
            displayName: `${data.firstName} ${data.lastName}`,
            loginMethod: LoginMethod.SSO,
          });
          result = await baseQuery(args, api, extraOptions);
        } else {
          saveLocalUser();
          redirectToLogin();
        }
      } else {
        saveLocalUser();
        redirectToLogin();
      }
    }
    return result;
  };

// Create base queries for each API domain
const surveyBaseQuery = createBaseQuery(surveyBaseURL);
const userBaseQuery = createBaseQuery(userBaseURL);
const mediaBaseQuery = createBaseQuery(mediaBaseURL);
const refreshBaseQuery = createBaseQuery(userBaseURL, false); // Use userBaseURL for refresh

// Export base queries with reauth logic for each API
export const surveyBaseQueryWithReauth = createBaseQueryWithReauth(
  surveyBaseQuery,
  refreshBaseQuery,
);
export const userBaseQueryWithReauth = createBaseQueryWithReauth(
  userBaseQuery,
  refreshBaseQuery,
);
export const mediaBaseQueryWithReauth = createBaseQueryWithReauth(
  mediaBaseQuery,
  refreshBaseQuery,
);

// Example: Extend API services from these base queries
// import { createApi } from "@reduxjs/toolkit/query/react";
export const surveyApi = createApi({
  reducerPath: "surveyApi",
  baseQuery: surveyBaseQueryWithReauth,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  endpoints: () => ({}),
});
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: userBaseQueryWithReauth,
  tagTypes: [
    "TeamUsers",
    "CustomUsers",
    "CollabUsers",
    "AllUsers",
    "LeaseCompLayouts",
    "TeamPropertyLayouts",
    "TableConfigs",
  ],
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  endpoints: () => ({}),
});
export const mediaApi = createApi({
  reducerPath: "mediaApi",
  baseQuery: mediaBaseQueryWithReauth,
  tagTypes: ["Media"],
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  endpoints: () => ({}),
});
export const bingApi = createApi({
  reducerPath: "bingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://dev.virtualearth.net/REST/V1/",
  }),
  endpoints: () => ({}),
});
export const mapApi = createApi({
  reducerPath: "mapApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.DEV ? "/" : mapBaseURL,
  }),
  endpoints: () => ({}),
});
